import * as Commons from "./Commons";
import _ from "lodash";
import { Listenable } from "./injectListener";

const maxRecursionCount = 100;
let logAllEvents = false;

type Constructor<T = {}> = new (...args: any[]) => T;

interface ListenerData {
  name: string;
  list: Function[];
  listOnce: Function[];
}

/**
 * Fügt einer Klasse per ES6-Mixin Listener Fähigkeit hinzu,
 * welche derselben Syntax wie jQuerys Eventsystem folgen.
 *
 * @template {{new (...args: any[]) => {}} Super
 * @param {Super} superClass
 *
 * @example
 * const dummy = new (ListenerMixin(THREE.Objekt3D))();
 * // Das Objekt hat daraufhin folgende zusätzliche Methoden:
 * dummy.on('feuer',()=>console.log('es brennt!'));
 * dummy.on('nett',()=>console.log('wie schön!'));
 * dummy.trigger('feuer');
 * dummy.off('feuer');
 *
 */
export const ListenerMixin = <TBase extends Constructor>(superClass: TBase = class {} as any) =>
  class ListenerMix extends superClass implements Listenable {
    /** @type {Object} */
    _listeners: Record<string, ListenerData>;
    /** @type {number} */
    _recursionCounter: number;
    _logEvents: boolean;

    // To satisfy Listenable interface (optional property) if checked strictly,
    // though we use _listeners validly.
    _LISTENERS?: Record<string, { name: string; list: Function[]; listOnce: Function[] }>;

    /**
     * @param args
     */
    constructor(...args: any[]) {
      super(...args);
      this._listeners = {};
      this._recursionCounter = 0;
      this._logEvents = false;
    }

    /**
     * @param name
     */
    _ensureListeners(name: string): void {
      if (this._listeners[name]) {
        return;
      }
      this._listeners[name] = {
        name: name,
        list: [],
        listOnce: [],
      };
    }

    /**
     * @param name
     * @param list
     * @param args
     */
    _callThem(name: string, list: Function[], args: any[]): void {
      if (this._recursionCounter > maxRecursionCount) {
        throw (
          'trigger("' +
          name +
          '") exceeds ' +
          maxRecursionCount +
          " recursions (might lead to stackoverflow)"
        );
      }
      try {
        this._recursionCounter++;
        const length = list.length;
        for (let i = 0; i < length; i++) {
          list[i].apply(this, args);
        }
      } finally {
        this._recursionCounter--;
      }
    }

    /**
     * ruft alle Listener zu einem Namen auf mit optionalen Parametern
     * @param {string} Event-Name
     * @param {...any} args
     * @returns {self}
     */
    trigger(name: string, ...args: any[]): this {
      if (this._logEvents || logAllEvents) {
        console.log(
          "event: " +
            ((this as any).name ? (this as any).name : Commons.typeName(this)) +
            "." +
            name,
        );
      }
      // console.log(Commons.typeName(this)+'.trigger(\''+name+'\','+args.join(',')+')');
      this._ensureListeners(name);
      const _listeners = this._listeners[name];
      this._callThem(name, _listeners.list, args);
      this._callThem(name, _listeners.listOnce, args);
      if (_listeners.listOnce.length > 0) {
        _listeners.listOnce = [];
      }
      return this;
    }

    /**
     * registriert einen Listener, der immer aufgerufen wird
     * @param {string} Event-Name
     * @param {function} callback der zu registrierende Callback / Listener
     * @param {[]} cleanUpList optional: falls übergeben, ein Array, in das eine Cleanup-Methode gepushed wird, welche diesen Listener wieder abräumt.
     * @returns {self}
     */
    on(name: string, callback: Function, cleanUpList: Function[] | undefined = undefined): this {
      if (!_.isFunction(callback)) {
        throw new Error("callback is not a function");
      }
      this._ensureListeners(name);
      this._listeners[name].list.push(callback);
      if (cleanUpList) {
        cleanUpList.push(() => this.off(name, callback));
      }
      return this;
    }

    /**
     * registriert einen Listener, der genau ein Mal aufgerufen wird
     * @param {string} Event-Name
     * @param {function} callback der zu registrierende Callback / Listener
     * @param {[]} cleanUpList optional: falls übergeben, ein Array, in das eine Cleanup-Methode gepushed wird, welche diesen Listener wieder abräumt.
     * @returns {self}
     */
    onOnce(
      name: string,
      callback: Function,
      cleanUpList: Function[] | undefined = undefined,
    ): this {
      if (!_.isFunction(callback)) {
        throw new Error("callback is not a function");
      }
      this._ensureListeners(name);
      this._listeners[name].listOnce.push(callback);
      return this;
    }

    /**
     * deregistriert einen Listener (nur solche, die immer aufgerufen würden)
     * @param {string} Event-Name
     * @param {function} callback der zu deregistrierende Callback / Listener
     * @returns {self}
     */
    off(name: string, callback?: Function): this {
      if (this._listeners && this._listeners[name]) {
        if (callback) {
          if (!_.isFunction(callback)) {
            throw new Error("callback is not a function");
          }
          _.remove(this._listeners[name].list, (x) => x === callback);
        } else {
          delete this._listeners[name];
        }
      }
      return this;
    }

    /**
     * deregistriert einen Listener (nur solche, die nur einmal aufgerufen werden)
     * @param {string} Event-Name
     * @param {function} callback der zu deregistrierende Callback / Listener
     * @returns {self}
     */
    offOnce(name: string, callback?: Function): this {
      if (this._listeners && this._listeners[name]) {
        if (callback) {
          if (!_.isFunction(callback)) {
            throw new Error("callback is not a function");
          }
          _.remove(this._listeners[name].listOnce, (x) => x === callback);
        } else {
          delete this._listeners[name];
        }
      }
      return this;
    }

    /**
     * damit ListenerMixins dasselbe Interface wie HTMLElement hat
     * @param {...any} args
     */
    addEventListener(name: string, callback: Function): void {
      this.on(name, callback);
    }

    /**
     * damit ListenerMixins dasselbe Interface wie HTMLElement hat
     * @param {...any} args
     */
    removeEventListener(name: string, callback: Function): void {
      this.off(name, callback);
    }
  };
